import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import {
  PRIORITIES,
  PRIORITY_LABEL,
  SEVERITIES,
  TICKET_TYPES,
  STATUS_LABEL,
  ROLES,
} from "@/lib/constants";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const EDITABLE = {
  [ROLES.SUPER_ADMIN]: new Set([
    "title",
    "description",
    "business_impact",
    "affected_url",
    "ticket_type",
    "priority",
    "severity",
  ]),

  [ROLES.SUPPORT_ADMIN]: new Set([
    "ticket_type",
    "priority",
    "severity",
  ]),

  [ROLES.SUPPORT_ENGINEER]: new Set([
    "description",
    "business_impact",
    "affected_url",
  ]),

  [ROLES.BUSINESS_ADMIN]: new Set([
    "title",
    "description",
    "business_impact",
    "affected_url",
    "ticket_type",
    "priority",
    "severity",
  ]),

  [ROLES.BUSINESS_USER]: new Set([
    "title",
    "description",
    "business_impact",
    "affected_url",
  ]),

  [ROLES.CLIENT_ADMIN]: new Set([
    "title",
    "description",
    "business_impact",
    "affected_url",
    "ticket_type",
    "priority",
    "severity",
  ]),

  [ROLES.CLIENT_USER]: new Set([
    "title",
    "description",
    "business_impact",
    "affected_url",
  ]),
};

export default function TicketEditDialog({
  open,
  onOpenChange,
  ticket,
  role,
  onSaved,
}) {
  const [form, setForm] = useState({});
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const normalizedRole = String(role || "")
    .trim()
    .toLowerCase();

  const clientUserFallback = new Set([
    "title",
    "description",
    "business_impact",
    "affected_url",
  ]);

  const clientAdminFallback = new Set([
    "title",
    "description",
    "business_impact",
    "affected_url",
    "ticket_type",
    "priority",
    "severity",
  ]);

  const allowed =
    EDITABLE[role] ||
    EDITABLE[normalizedRole] ||
    (normalizedRole === "client_user"
      ? clientUserFallback
      : normalizedRole === "client_admin"
        ? clientAdminFallback
        : new Set());

  useEffect(() => {
    if (!open || !ticket) return;

    setForm({
      title: ticket.title || "",
      description: ticket.description || "",
      business_impact: ticket.business_impact || "",
      affected_url: ticket.affected_url || "",
      ticket_type: ticket.ticket_type || "incident",
      priority: ticket.priority || "P3",
      severity: ticket.severity || "moderate",
    });

    setReason("");
  }, [open, ticket]);

  const set = (key, value) =>
    setForm((current) => ({ ...current, [key]: value }));

  const save = async () => {
    if (allowed.has("title") && !form.title?.trim()) {
      return toast.error("Title is required");
    }

    if (allowed.has("description") && !form.description?.trim()) {
      return toast.error("Description is required");
    }

    const changes = {};

    Object.keys(form).forEach((key) => {
      if (!allowed.has(key)) return;

      const original = ticket?.[key] || "";
      const current = form[key] || "";

      if (String(original) !== String(current)) {
        changes[key] =
          typeof current === "string" && !current.trim()
            ? null
            : typeof current === "string"
              ? current.trim()
              : current;
      }
    });

    if (!Object.keys(changes).length) {
      return toast.info("No changes detected");
    }

    setSaving(true);

    try {
      await api.patch(`/tickets/${ticket.id}`, {
        changes,
        reason: reason.trim() || null,
      });

      toast.success("Ticket updated successfully");
      onOpenChange(false);
      onSaved?.();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  if (!allowed.size && !open) return null;

  const canEdit = (field) => allowed.has(field);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="left-0 top-0 h-screen w-screen max-w-none translate-x-0 translate-y-0 gap-0 overflow-hidden rounded-none border-0 p-0 sm:rounded-none"
        data-testid="edit-ticket-dialog"
      >
        <div className="flex h-full min-h-0 flex-col bg-background">
          <div className="shrink-0 border-b border-border bg-card px-6 py-5 pr-14">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl font-extrabold">
                Edit ticket {ticket?.ticket_number}
              </DialogTitle>
              <DialogDescription>
                Every change is recorded in the ticket timeline and audit log.
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            <div className="mx-auto w-full max-w-5xl px-6 py-7">
              <div className="grid grid-cols-1 gap-5 rounded-xl border border-border bg-card p-6 md:grid-cols-2">
                {canEdit("title") && (
                  <div className="md:col-span-2">
                    <Label>Title *</Label>
                    <Input
                      data-testid="edit-ticket-title"
                      maxLength={100}
                      value={form.title || ""}
                      onChange={(e) => set("title", e.target.value)}
                    />
                  </div>
                )}

                {canEdit("ticket_type") && (
                  <div>
                    <Label>Issue type</Label>
                    <Select
                      value={form.ticket_type}
                      onValueChange={(v) => set("ticket_type", v)}
                    >
                      <SelectTrigger data-testid="edit-ticket-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TICKET_TYPES.map((type) => (
                          <SelectItem key={type} value={type}>
                            {STATUS_LABEL[type] || type.replace(/_/g, " ")}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {canEdit("priority") && (
                  <div>
                    <Label>Priority</Label>
                    <Select
                      value={form.priority}
                      onValueChange={(v) => set("priority", v)}
                    >
                      <SelectTrigger data-testid="edit-ticket-priority">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PRIORITIES.map((priority) => (
                          <SelectItem key={priority} value={priority}>
                            {PRIORITY_LABEL[priority]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {canEdit("severity") && (
                  <div>
                    <Label>Severity</Label>
                    <Select
                      value={form.severity}
                      onValueChange={(v) => set("severity", v)}
                    >
                      <SelectTrigger data-testid="edit-ticket-severity">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SEVERITIES.map((severity) => (
                          <SelectItem key={severity} value={severity}>
                            {severity.charAt(0).toUpperCase() + severity.slice(1)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {canEdit("description") && (
                  <div className="md:col-span-2">
                    <Label>Description *</Label>
                    <Textarea
                      data-testid="edit-ticket-description"
                      rows={7}
                      value={form.description || ""}
                      onChange={(e) => set("description", e.target.value)}
                    />
                  </div>
                )}

                {canEdit("business_impact") && (
                  <div className="md:col-span-2">
                    <Label>Business impact</Label>
                    <Textarea
                      data-testid="edit-ticket-impact"
                      rows={3}
                      value={form.business_impact || ""}
                      onChange={(e) => set("business_impact", e.target.value)}
                    />
                  </div>
                )}

                {canEdit("affected_url") && (
                  <div className="md:col-span-2">
                    <Label>Affected URL</Label>
                    <Input
                      data-testid="edit-ticket-url"
                      value={form.affected_url || ""}
                      onChange={(e) => set("affected_url", e.target.value)}
                      placeholder="https://example.com/page"
                    />
                  </div>
                )}

                <div className="md:col-span-2">
                  <Label>Reason for change</Label>
                  <Textarea
                    data-testid="edit-ticket-reason"
                    rows={3}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Briefly explain why this ticket is being updated."
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="shrink-0 border-t border-border bg-card px-6 py-4">
            <div className="mx-auto flex w-full max-w-5xl justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                data-testid="edit-ticket-save"
                onClick={save}
                disabled={saving}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                {saving ? "Saving..." : "Save changes"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
